// HouseholdPage — /households/:householdId — one household, everything about
// it. (The restructured CaseFilePage; the case file's durable-case block,
// actions, signals and RRA posture are all still here.)
//
//   Miles (Q6): "one household detail page which should focus first on where
//   it stands but list all the items related to that household. In motion
//   items get more details displayed. Each item can be expanded into a version
//   of the details page as from Sent Items — just different paths to get at the
//   same view."
//
// So the page reads top to bottom as: who and where → WHERE IT STANDS and what
// can be done now → the items, in motion first and open, finished last and
// collapsed to a line with the two things a finished document is for (read it,
// download it) → signals → what has been happening.
//
// Three rules it will not break:
//
//   1. NOTHING HERE NAMES A STAGE OR A NEXT STEP THE DATA DID NOT CREATE
//      (§1, §8.4). The standing line comes from the API; this page renders it.
//      A household whose whole history with us is one renewal shows one
//      renewal and no application language anywhere.
//   2. A PRE-CASE HOUSEHOLD IS A NORMAL PAGE (T15). `group:<groupId>` has no
//      case, so nothing on this page may go looking for a docket, a case
//      snapshot or an intake for one — the household route is the ONLY request
//      this page makes for the household, and cases are minted by the send
//      paths, never by a page view.
//   3. AN EMPTY BLOCK IS NEVER RENDERED. A heading over nothing reads as data
//      we failed to load (the WEL-144 C2 rule), so a household with no signals
//      has no Signals heading.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import { InspectorDrawer, OcError, OcLoading, SignalRowView } from '@/components/oc/bits';
import { SentItemList } from '@/components/sentItems/SentItemList';
import { SentItemPanel } from '@/components/sentItems/SentItemPanel';
import { rowInMotion } from '@/components/sentItems/sentItemsView';
import { formatWhen, type ActivityEvent } from '@/lib/commandCenterClient';
import { describeWhere, describeWithheldKinds } from '@/lib/viewDocs';
import { fetchDocFile } from '@/lib/viewDocsClient';
import { fetchSentItem } from '@/lib/sentItemsClient';
import { fetchSentItemsBatch } from '@/lib/sentItemsBatchClient';
import type { SentItemDetailResponse, SentItemRow } from '@/lib/sentItemsClient';
import type { HouseholdDetailResponse } from '@/lib/sentItemsClient';
import {
  RELATIONSHIP_LABEL,
  RELATIONSHIP_TONE,
  fetchHousehold,
  householdPath,
  householdIdForGroup,
  isPreCaseHouseholdId,
  standingDotTone,
} from '@/lib/householdsClient';
import { fixtureHouseholdDetail } from '@/lib/householdsFixtures';
import { fixtureDetail } from '@/lib/sentItemsFixtures';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `${fallback} (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Household-wide activity (§8.3)
//
// ⚠️ THIS FEED IS NEWEST **FIRST**, and it is the OPPOSITE of
// `SentItemDetailResponse.activity`, which is newest LAST (T18) because one
// document's story reads down the page. Both directions are deliberate and
// documented on their own types. Render each in the order it arrives: do not
// reverse this one, and do not "normalise" the two to match each other. (The
// API caps it at 50 — `activityFor()` in routes/commandCenter.ts.)
//
// `HouseholdDetailResponse.activity` is REQUIRED as of W0's contract fix
// (9e4c1d3): an empty array is the honest "nothing has happened yet", where an
// optional field would let this section quietly never ship — a silent
// regression from the case file it replaces.
//
// The runtime narrowing below is kept anyway, and is NOT a workaround: this
// branch was cut before that fix landed, so the guard is what lets the page
// compile and behave identically on both sides of the integrator's merge. It
// costs one filter over at most 50 rows and can be deleted once the contract
// is merged. What it must never become is a second source of the field — the
// household route is the only place this page gets anything, because a
// pre-case `group:<groupId>` household must never trigger a case-file fetch
// (T15).
// ---------------------------------------------------------------------------

function householdActivity(detail: HouseholdDetailResponse): ActivityEvent[] {
  const raw = (detail as { activity?: unknown }).activity;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is ActivityEvent =>
      !!e &&
      typeof e === 'object' &&
      typeof (e as ActivityEvent).at === 'string' &&
      typeof (e as ActivityEvent).text === 'string',
  );
}

// ---------------------------------------------------------------------------
// Per-item details
// ---------------------------------------------------------------------------

export type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; detail: SentItemDetailResponse }
  | { status: 'error'; message: string };

/**
 * Every in-motion item's details, in ONE request (sol finding 5).
 *
 * This page opens with every in-motion row already expanded (§8.3), and each
 * expansion is blocks ①–③ of that item's detail. It used to fetch them one id at
 * a time, so a household with sixteen open items fired sixteen detail requests
 * the moment it landed — each of them loading its own scoped sources, awaiting
 * its own snapshot and querying its own events. That is the concurrent-statement
 * fan-out the whole redesign exists to avoid.
 *
 * Exported because it IS the page's live loading path, and a test that asserts
 * "sixteen items, one request" has to be able to call the thing that does it.
 * (Effects do not run under react-dom/server, so the page itself cannot be
 * asserted about here — T21.)
 *
 * An id that comes back as a failure becomes an ERROR state on its own row, not
 * a missing one: a row that could not load has to say so. An id the server
 * mentions in neither list gets the same treatment rather than sitting on a
 * skeleton forever.
 */
export async function fetchDetailsForItems(
  itemIds: string[],
): Promise<Record<string, DetailState>> {
  const out: Record<string, DetailState> = {};
  const res = await fetchSentItemsBatch(itemIds);
  for (const detail of res.items) out[detail.row.itemId] = { status: 'ready', detail };
  for (const failure of res.failures) {
    out[failure.itemId] = { status: 'error', message: failure.message };
  }
  for (const itemId of itemIds) {
    out[itemId] ??= { status: 'error', message: "Couldn't load this item's details" };
  }
  return out;
}

/**
 * Which rows open inline (§8.3): every row still in motion.
 *
 * `rowInMotion` trusts the server's own `inMotion` (§5.6 is ONE pure function,
 * server-side) — this page does not have a second opinion about whether
 * something is finished. A withheld stub is never expanded: it is not a door.
 */
export function inMotionItemIds(items: readonly SentItemRow[]): string[] {
  return items.filter((row) => rowInMotion(row) && !row.redacted).map((row) => row.itemId);
}

/**
 * The inline expansion of an in-motion row (§8.3, blocks ①–③).
 *
 * It is a SKELETON while it loads rather than a spinner over the page: the
 * household's items are the page, and making the whole file wait on N detail
 * fetches would mean the list Miles asked to see first is the last thing to
 * arrive. A detail that fails says so on its own row and leaves everything
 * else alone.
 */
function ItemExpansion({
  state,
  onChanged,
}: {
  state: DetailState | undefined;
  onChanged: () => void;
}): React.JSX.Element {
  if (!state || state.status === 'loading') {
    return <div className="oc-meta">Loading the details…</div>;
  }
  if (state.status === 'error') {
    return <div className="oc-meta">{state.message}</div>;
  }
  return (
    <SentItemPanel
      detail={state.detail}
      blocks={['standing', 'recipients', 'activity']}
      onChanged={onChanged}
    />
  );
}

/**
 * A finished row's two affordances, on the row itself (§8.3): read it, or take
 * a copy.
 *
 * The download is gated on `row.viewable` — the row's own answer, not "is
 * there a URL" — so a W-9 can never acquire one by a route that only checks
 * whether a file exists (T6). The bytes go through `fetchDocFile` with the
 * session token, because /file is authenticated and a bare href would 401.
 *
 * The `(kind, id)` pair that addresses the document lives on the item's
 * DETAIL, not on its row, so the click resolves it — a finished row does not
 * pre-fetch a detail nobody asked for.
 */
function FinishedRowActions({
  row,
  cached,
  loadDetail,
}: {
  row: SentItemRow;
  cached: SentItemDetailResponse | null;
  loadDetail: (itemId: string) => Promise<SentItemDetailResponse>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const detail = cached ?? (await loadDetail(row.itemId));
      const version = detail.versions.find((v) => v.current) ?? detail.versions[0];
      if (!version) {
        toast.info('There is no generated document to download for this item.');
        return;
      }
      const blob = await fetchDocFile(version.docKind, version.docId, null, detail.file?.url);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = detail.file?.filename ?? `${row.kindLabel.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't download the document"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
      {row.viewable && (
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={busy}
          onClick={() => void download()}
        >
          {busy ? 'Preparing…' : 'Download'}
        </button>
      )}
      <Link
        className="btn"
        data-variant="ghost"
        data-size="sm"
        to={`/items/${encodeURIComponent(row.itemId)}`}
      >
        Full Details
      </Link>
    </span>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function HouseholdPage(): React.JSX.Element | null {
  const { householdId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const fixturesMode = Boolean(import.meta.env?.DEV) && searchParams.get('fixtures') === '1';
  const openId = searchParams.get('open');

  const [data, setData] = useState<HouseholdDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const liveRef = useRef(0);

  const [details, setDetails] = useState<Record<string, DetailState>>({});
  const requested = useRef(new Set<string>());

  const reload = useCallback(() => {
    requested.current.clear();
    setDetails({});
    setReloadToken((n) => n + 1);
  }, []);

  // ---- the household ----
  useEffect(() => {
    if (!householdId) return;
    if (fixturesMode) {
      const hit = fixtureHouseholdDetail(householdId);
      setData(hit);
      setError(hit ? null : 'No fixture household for that id.');
      setLoading(false);
      return;
    }
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetchHousehold(householdId);
        if (liveRef.current !== token) return;
        setData(res);
      } catch (err) {
        if (liveRef.current !== token) return;
        setError(errorMessage(err, "Couldn't load this household"));
      } finally {
        if (liveRef.current === token) setLoading(false);
      }
    })();
  }, [householdId, fixturesMode, reloadToken]);

  /**
   * ONE item's details, on demand — a finished row's Download click resolving
   * the `(kind, id)` pair it needs. Never the page's bulk path.
   */
  const loadDetail = useCallback(
    async (itemId: string): Promise<SentItemDetailResponse> => {
      if (fixturesMode) {
        const hit = fixtureDetail(itemId);
        if (!hit) throw new Error('No fixture detail for that item.');
        return hit;
      }
      return fetchSentItem(itemId);
    },
    [fixturesMode],
  );

  /**
   * Ask for every item we do not already have — in ONE request (finding 5).
   *
   * `requested` is still the "ever asked" set, so a re-render, a second effect
   * or the drawer opening a row that is already expanded costs nothing.
   */
  const ensureDetails = useCallback(
    (itemIds: string[]) => {
      const wanted = itemIds.filter((id) => id && !requested.current.has(id));
      if (wanted.length === 0) return;
      for (const id of wanted) requested.current.add(id);
      setDetails((prev) => {
        const next = { ...prev };
        for (const id of wanted) next[id] = { status: 'loading' };
        return next;
      });
      void (async () => {
        try {
          const loaded = fixturesMode
            ? Object.fromEntries(
                wanted.map((id) => {
                  const hit = fixtureDetail(id);
                  return [
                    id,
                    hit
                      ? ({ status: 'ready', detail: hit } as DetailState)
                      : ({ status: 'error', message: 'No fixture detail for that item.' } as DetailState),
                  ];
                }),
              )
            : await fetchDetailsForItems(wanted);
          setDetails((prev) => ({ ...prev, ...loaded }));
        } catch (err) {
          // The whole request failed (not one item): every row it was carrying
          // says so, rather than sitting on a skeleton forever.
          const message = errorMessage(err, "Couldn't load these items' details");
          setDetails((prev) => {
            const next = { ...prev };
            for (const id of wanted) next[id] = { status: 'error', message };
            return next;
          });
        }
      })();
    },
    [fixturesMode],
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const inMotionIds = useMemo(() => inMotionItemIds(items), [items]);

  // In-motion rows are open on arrival (§8.3), so their details are fetched as
  // soon as the household lands — ONE request for all of them, never blocking
  // the page.
  useEffect(() => {
    ensureDetails(inMotionIds);
  }, [inMotionIds, ensureDetails]);

  // The drawer's item, when it is not one of the expanded ones. Same path, one
  // id — so there is one loader to reason about rather than two.
  useEffect(() => {
    if (openId) ensureDetails([openId]);
  }, [openId, ensureDetails]);

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const openItem = useCallback(
    (itemId: string) => updateParams((p) => p.set('open', itemId)),
    [updateParams],
  );
  const closeItem = useCallback(() => updateParams((p) => p.delete('open')), [updateParams]);

  if (loading) return <OcLoading label="Loading the household…" />;
  if (error) {
    return (
      <OcError>
        <div>{error}</div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={reload}
          >
            Retry
          </button>
        </div>
      </OcError>
    );
  }
  if (!data) return null;

  return (
    <HouseholdFile
      detail={data}
      details={details}
      loadDetail={loadDetail}
      openId={openId}
      onOpenItem={openItem}
      onCloseItem={closeItem}
      onChanged={reload}
    />
  );
}

// ---------------------------------------------------------------------------
// The file itself
// ---------------------------------------------------------------------------

export interface HouseholdFileProps {
  detail: HouseholdDetailResponse;
  /** What is known about each item's details, keyed by itemId. */
  details: Record<string, DetailState>;
  /** Resolve one item's details on demand (a finished row's download). */
  loadDetail: (itemId: string) => Promise<SentItemDetailResponse>;
  openId: string | null;
  onOpenItem: (itemId: string) => void;
  onCloseItem: () => void;
  /** Something changed under us — re-read the household and its items. */
  onChanged: () => void;
}

/**
 * The household, rendered from what has already been loaded.
 *
 * Split from the page for the same reason W4 split `SentItemPanel` from
 * `SentItemsPage`: a component that fetches renders "Loading…" under
 * react-dom/server and cannot be asserted about. Everything below is a pure
 * function of `detail` + `details`.
 */
export function HouseholdFile({
  detail: data,
  details,
  loadDetail,
  openId,
  onOpenItem,
  onCloseItem,
  onChanged,
}: HouseholdFileProps): React.JSX.Element {
  const items = data.items ?? [];
  const inMotionIds = inMotionItemIds(items);

  const { header } = data;
  const preCase = isPreCaseHouseholdId(data.householdId);
  const adults = header.people.filter((p) => !p.isMinor).length;
  const minors = header.people.length - adults;
  const whereBits = [
    describeWhere(header.propertyLabel, header.unitLabel),
    header.afStatusLabel,
  ].filter((v): v is string => !!v);

  // The builder, right in the header (Miles, 2026-08-28): a converted tenancy
  // no longer sits on an applications list, so the household file is where
  // staff land when they need to send a corrected lease — and the way in has to
  // be obvious from here, not three panels down. The href is the server's own
  // `builder` action, so this link and the actions below can never disagree.
  const builderAction =
    header.actions.find((a) => a.id === 'builder' && a.enabled && !!a.href) ?? null;

  const withheldNote = describeWithheldKinds(data.withheld ?? []);
  const activity = householdActivity(data);
  const durableCase = data.durableCase ?? null;
  const openRow = items.find((r) => r.itemId === openId) ?? null;
  const openState = openId ? details[openId] : undefined;

  const cachedDetail = (itemId: string): SentItemDetailResponse | null => {
    const state = details[itemId];
    return state && state.status === 'ready' ? state.detail : null;
  };

  return (
    <div className="oc-page si-page">
      {/* ---------- Who and where ---------- */}
      <header className="oc-panel">
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <h1 className="oc-page-title">{header.name}</h1>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            {builderAction?.href && (
              <Link
                className="btn"
                data-variant="secondary"
                data-size="sm"
                to={builderAction.href}
                title="Open the lease builder for this household."
              >
                Lease builder
              </Link>
            )}
            {header.freshAsOf && (
              <span className="oc-meta">AppFolio · as of {formatWhen(header.freshAsOf)}</span>
            )}
          </span>
        </div>

        <div
          className="oc-meta"
          style={{
            marginTop: 'var(--space-2)',
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span className="pill" data-tone={RELATIONSHIP_TONE[header.relationship]}>
            {RELATIONSHIP_LABEL[header.relationship]}
          </span>
          {whereBits.map((bit, i) => (
            <span key={i}>
              {i > 0 && <span aria-hidden="true">· </span>}
              {bit}
            </span>
          ))}
          {header.people.length > 0 && (
            <span>
              <span aria-hidden="true">· </span>
              {adults} adults, {minors} minors
            </span>
          )}
        </div>

        {/* The CURRENT executed lease's own summary — never AppFolio's, which
            does not carry the current term or rent (a known mirror gap). */}
        {header.leaseTerm && (
          <div className="oc-meta" style={{ marginTop: 'var(--space-2)' }}>
            Current lease · {header.leaseTerm.start} – {header.leaseTerm.end} ·{' '}
            {header.leaseTerm.rent}
          </div>
        )}

        {header.people.length > 0 && (
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'var(--space-3)' }}
          >
            {header.people.map((p, i) => (
              <span
                key={i}
                className="oc-token"
                {...(p.isMinor ? { style: { color: 'var(--fg-muted)' } } : {})}
              >
                {p.name} — {p.isLead ? 'lead · ' : ''}
                {p.type}
                {p.isMinor
                  ? ` · minor${p.comingOfAge ? `, 18 on ${p.comingOfAge}` : ''}`
                  : ''}
              </span>
            ))}
          </div>
        )}

        {/* ---------- Where it stands, and what can be done now ---------- */}
        <div className="si-standing" style={{ marginTop: 'var(--space-4)' }}>
          <span className="dot" data-tone={standingDotTone(header.standing.tone)} />
          <span className="si-standing-step">{header.standing.line}</span>
        </div>

        {header.actions.length > 0 && (
          <div className="si-actions">
            {header.actions.map((a) =>
              a.enabled && a.href ? (
                <Link key={a.id} className="btn" data-variant="secondary" data-size="sm" to={a.href}>
                  {a.label}
                </Link>
              ) : (
                <span key={a.id} style={{ display: 'inline-flex', flexDirection: 'column' }}>
                  <button type="button" className="btn" data-variant="secondary" data-size="sm" disabled>
                    {a.label}
                  </button>
                  {a.blockedReason && (
                    <span className="oc-meta" style={{ marginTop: 2 }}>
                      {a.blockedReason}
                    </span>
                  )}
                </span>
              ),
            )}
          </div>
        )}
      </header>

      {/* ---------- The case, across lease cycles ---------- */}
      {durableCase && (
        <section className="oc-panel">
          <div className="oc-kicker">Case — across lease cycles</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'var(--weight-medium)' }}>
              {durableCase.householdName ?? 'This case'}
            </span>
            <span className="oc-meta">
              started {formatWhen(durableCase.startedAt)}
              {durableCase.lastLeaseAt
                ? ` · last lease sent ${formatWhen(durableCase.lastLeaseAt)}`
                : ''}
              {' · '}
              {durableCase.members.length === 1
                ? '1 lease cycle'
                : `${durableCase.members.length} lease cycles`}
            </span>
          </div>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'var(--space-2)' }}
          >
            {durableCase.members.map((m) => {
              const label = `${m.linkedVia === 'renewal' ? 'Renewal' : 'Original'}${
                m.isCurrent ? ' · current' : ''
              }`;
              return m.isThisGroup ? (
                <span key={m.groupId} className="oc-token">
                  {label} — this file
                </span>
              ) : (
                <Link
                  key={m.groupId}
                  className="oc-token"
                  to={householdPath(householdIdForGroup(m.groupId))}
                  title="Open this lease cycle's file"
                >
                  {label}
                  {m.linkedAt ? ` · ${formatWhen(m.linkedAt)}` : ''}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------- Items — the household's whole file ---------- */}
      <section className="oc-panel">
        <div className="oc-kicker">
          Items — everything sent, and everything being prepared
        </div>
        <SentItemList
          items={items}
          onOpen={onOpenItem}
          panelOpen={Boolean(openId)}
          expandedIds={inMotionIds}
          renderExpanded={(row) => (
            <ItemExpansion state={details[row.itemId]} onChanged={onChanged} />
          )}
          renderRowActions={(row) =>
            rowInMotion(row) ? null : (
              <FinishedRowActions
                row={row}
                cached={cachedDetail(row.itemId)}
                loadDetail={loadDetail}
              />
            )
          }
          emptyText={
            preCase
              ? 'Nothing sent yet. Building the lease is where this household starts.'
              : 'Nothing sent yet.'
          }
        />
      </section>

      {/* ---------- Signals ---------- */}
      {data.signals.length > 0 && (
        <section className="oc-panel">
          <div className="oc-kicker">Signals</div>
          {data.signals.map((s) => (
            <SignalRowView key={s.id} signal={s} onChanged={onChanged} />
          ))}
        </section>
      )}

      {/* The standing C9 line: keyed on the VIEWER, on every household file,
          identical whether or not this household has an accommodation. */}
      {withheldNote && <div className="oc-meta si-withheld">{withheldNote}</div>}

      {/* ---------- Activity ---------- */}
      {activity.length > 0 && (
        <section className="oc-panel">
          <div className="oc-kicker">Activity</div>
          {activity.map((ev, i) => (
            <div key={i} className="oc-feed-item">
              <span className="oc-feed-time">{formatWhen(ev.at)}</span>
              <span>{ev.text}</span>
            </div>
          ))}
        </section>
      )}

      {/* ---------- The same panel Sent Items opens, from the other path ---- */}
      {openId && (
        <InspectorDrawer
          title={openRow?.subject || 'Loading…'}
          subtitle={
            openRow
              ? [openRow.kindLabel, describeWhere(openRow.propertyLabel, openRow.unitLabel)]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          onClose={onCloseItem}
        >
          {!openState || openState.status === 'loading' ? (
            <OcLoading label="Loading the item…" />
          ) : openState.status === 'error' ? (
            <OcError>{openState.message}</OcError>
          ) : (
            <SentItemPanel detail={openState.detail} onChanged={onChanged} />
          )}
        </InspectorDrawer>
      )}
    </div>
  );
}
